/**
 * Obligation sources + the decision editor ("vN") — V98-4-5 (D-98-3), V98-4 review.
 *
 *   card      Frameworks (always on) · AI legislation · case-law signals ·
 *             jurisdictions, from the recorded version; a frameworks-only org
 *             ceiling is ONE read-only line with its way forward (T4-H8:
 *             "Change org policy" for org admins, "Ask {org admin}" for
 *             everyone else); proposals; version history; "Releases · N
 *             active" (Legal, T4-H7); Export outcomes.
 *   editor    Legal only. "Open vN draft" / "Close draft" (T4-H2). Toggles,
 *             jurisdictions, proposals (accepting applies the change, T4-H6),
 *             a one-line rationale, re-review date + triggers. Recording is
 *             BLOCKED while any determination is open (one gate message, the
 *             submit aria-disabled and described by it; the server refuses too:
 *             409 cells_open); a valid submit confirms the diff from the
 *             recorded version first.
 *   propose   owner / admin: "Propose a change" (never a decision).
 *   release   Legal: waive privilege and release the rationale of recorded
 *             determinations to a named recipient; list + revoke releases.
 *   ask       non-Legal: "Ask Legal" (about one law × system); anyone below an
 *             org admin: ask the org admin to allow AI legislation.
 */
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PRIVILEGE_WAIVER_TEXT, REREVIEW_TRIGGERS } from '@aigovops/core';
import { Alert, Button, Checkbox, Field, Input, Select, Textarea } from '@aigovops/design-system';
import {
  askLegalAboutCell, askOrgAdmin, declineSourcesProposal, exportDeterminationsUrl, listPrivilegeReleases, proposeSourcesChange, recordSourcesDecision,
  releaseRationale, revokeRelease, saveSourcesDraft,
  type AiGrid, type DecisionView, type ObligationSourcesView, type PrivilegeReleaseView, type ReleaseRecipientKind,
} from '../../lib/ai-program-api';
import { ApiError } from '../../lib/api';
import { JURISDICTION_CHOICES, OUTCOME_LABEL, VISIBILITY_LABEL, cellTitle, decisionDiff, jurisdictionName, nextVersion, proposalChange, recordGate } from '../../lib/ai-program-model';
import { fmtDay, type Names } from '../../lib/today-model';
import { ActionDialog, Chip, domId, errorText } from '../today/shared';

const TRIGGER_LABEL: Record<string, string> = {
  new_system: 'A new system', law_amendment: 'A law amendment', new_market: 'A new market', case_law: 'A ruling', date: 'The date',
};
const onOff = (v: boolean | null | undefined) => (v ? 'On' : 'Off');
const fmtY = (d: string) => fmtDay(d, true);
export const releasesKey = (slug: string) => ['ai-releases', slug] as const;

/** Legal's releases (active / expired / revoked). */
export function useReleases(slug: string, enabled: boolean) {
  return useQuery({ queryKey: releasesKey(slug), queryFn: () => listPrivilegeReleases(slug), enabled, staleTime: 30_000, retry: false });
}

export function ObligationSourcesCard({ slug, sources, grid, names, isAdmin, onEdit, onPropose, onRelease, onReleases, onAskLegal, onAskPolicy, editorOpen, releasesActive }: {
  slug: string; sources: ObligationSourcesView; grid: AiGrid | null; names: Names; isAdmin: boolean;
  onEdit: () => void; onPropose: () => void; onRelease: () => void; onReleases: () => void; onAskLegal: () => void; onAskPolicy: () => void;
  editorOpen: boolean; releasesActive: number | null;
}) {
  const eff = sources.effective;
  const v = nextVersion(sources);
  const current = sources.current;
  const locked = !sources.ceiling.allows_legislation;
  const open = grid?.counts.open ?? 0;
  const legal = sources.legal_reviewer?.name ?? null;
  const policyBy = sources.ceiling.set_by_name ?? sources.policy_contact?.name ?? 'org admin';
  return (
    <section className="gp-ai-card" aria-labelledby="ai-sources-h">
      <div className="gp-ai-card-head">
        <h3 id="ai-sources-h" className="gp-card-h">Obligation sources</h3>
        <span className="gp-actions">
          {sources.can_decide
            // T4-H2: secondary while cells are still open; "Close draft" while it is open.
            ? <Button compact variant={editorOpen || open > 0 ? 'secondary' : 'primary'} aria-expanded={editorOpen} aria-controls="ai-editor" onClick={onEdit}>{editorOpen ? 'Close draft' : `Open v${v} draft`}</Button>
            : sources.can_propose && !locked ? <Button compact onClick={onPropose}>Propose a change</Button>
            : legal ? <span className="gp-ai-who">{legal}<button type="button" className="gp-linkbtn gp-linkbtn--compact" onClick={onAskLegal}>Ask Legal</button></span>
            : null}
        </span>
      </div>
      <dl className="gp-ai-sources">
        <div><dt>Frameworks</dt><dd>Always on</dd></div>
        <div id="ai-ceiling">
          <dt>AI legislation</dt>
          <dd>
            {locked ? (
              // T4-H8: one read-only line with its way forward.
              <span className="gp-ai-locked">
                <span>{`Off · Locked by org policy (${policyBy})`}</span>
                {sources.can_change_policy && sources.org_slug
                  ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`/o/${encodeURIComponent(sources.org_slug)}/settings`}>Change org policy</Link>
                  : sources.policy_contact ? <Button compact onClick={onAskPolicy}>{`Ask ${sources.policy_contact.name ?? 'the org admin'}`}</Button> : null}
              </span>
            ) : eff.legislation ? `On · ${eff.jurisdictions.length} jurisdiction${eff.jurisdictions.length === 1 ? '' : 's'}` : 'Off'}
          </dd>
        </div>
        {eff.legislation ? (
          <div><dt>Jurisdictions</dt><dd><span className="gp-ai-jur">{eff.jurisdictions.map((j) => <Chip key={j} text={jurisdictionName(j)} tone="outline" />)}</span></dd></div>
        ) : null}
        {locked ? null : <div><dt>Case-law signals</dt><dd>{onOff(eff.case_law)}</dd></div>}
        <div><dt>Decided</dt><dd>{current ? `v${current.version} · ${names.get(current.decided_by ?? '') ?? 'Legal'}${current.decided_at ? ` · ${fmtY(current.decided_at)}` : ''}${current.review_by ? ` · re-review by ${fmtY(current.review_by)}` : ''}` : 'Not yet'}</dd></div>
        {current?.rationale ? <div><dt>Rationale</dt><dd>{current.rationale}</dd></div> : current?.rationale_withheld ? <div><dt>Rationale</dt><dd className="gp-dim">Held by Legal</dd></div> : null}
        {sources.can_decide && releasesActive !== null ? (
          <div>
            <dt>Releases</dt>
            <dd><button type="button" className="gp-linkbtn gp-linkbtn--compact" onClick={onReleases}>{`${releasesActive} active`}</button></dd>
          </div>
        ) : null}
      </dl>
      {sources.proposals.length ? (
        <div>
          <h4 id="ai-prop-h" className="gp-ai-h4">{`Proposals · ${sources.proposals.length}`}</h4>
          <ul className="gp-ai-open-list" aria-labelledby="ai-prop-h">
            {sources.proposals.map((p) => (
              <li key={p.decision_id} id={`proposal-${p.decision_id}`}>
                <span>{p.note}</span>
                <span className="gp-dim">{`${names.get(p.proposed_by ?? '') ?? 'Onboarding'}${p.proposed_at ? ` · ${fmtY(p.proposed_at)}` : ''}`}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {sources.history.length ? (
        <div>
          <h4 id="ai-hist-h" className="gp-ai-h4">History</h4>
          <ul className="gp-ai-history" aria-labelledby="ai-hist-h">
            {sources.history.map((h) => (
              <li key={h.decision_id}>
                {[`v${h.version}`, h.state === 'draft' ? 'Draft' : h.state === 'replaced' ? 'Replaced' : 'Recorded', h.decided_at ? fmtY(h.decided_at) : null,
                  h.legislation_on ? h.jurisdictions.map(jurisdictionName).join(', ') || 'No jurisdictions' : 'Frameworks only'].filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {current && (isAdmin || sources.can_decide) ? (
        // T4-H10: no sentence — the action says it. Peer actions share one weight (compact secondary).
        <div className="gp-actions">
          <a className="gp-linkbtn gp-linkbtn--compact" href={exportDeterminationsUrl(slug)} download="ai-regulatory-determinations.json">Export outcomes</a>
          {sources.can_decide && grid?.cells.some((c) => c.determination?.state === 'recorded') ? <button type="button" className="gp-linkbtn gp-linkbtn--compact" onClick={onRelease}>Release rationale…</button> : null}
        </div>
      ) : null}
    </section>
  );
}

// ---- the vN editor --------------------------------------------------------------------------

export const OPEN_FIELDSET_ID = 'ai-open-fs';
export const decideButtonId = (cellId: string) => domId('ai-dec', cellId);

export function DecisionEditor({ slug, sources, grid, onClose, onOpenCell, onDone }: {
  slug: string; sources: ObligationSourcesView; grid: AiGrid | null; onClose: () => void;
  /** `returnTo`: where focus goes when the cell closes (the next Decide button, else the fieldset). */
  onOpenCell: (cellId: string, returnTo: string) => void;
  /** `close`: the version was recorded (the editor closes); otherwise it stays open and the data refreshes. */
  onDone: (message: string, close?: boolean) => void;
}) {
  const uid = useId();
  const base: DecisionView | null = sources.draft ?? sources.current;
  const v = nextVersion(sources);
  const locked = !sources.ceiling.allows_legislation;
  const [legislation, setLegislation] = useState(!!base?.legislation_on && !locked);
  const [caseLaw, setCaseLaw] = useState(!!base?.case_law_on && !locked);
  const [jurisdictions, setJurisdictions] = useState<string[]>(base?.jurisdictions ?? []);
  const [accepted, setAccepted] = useState<string[]>(sources.draft?.accepted_proposal_ids ?? []);
  const [rationale, setRationale] = useState(sources.draft?.rationale ?? '');
  const [visibility, setVisibility] = useState<'legal_only' | 'workspace' | 'external'>(sources.draft?.rationale_visibility ?? 'legal_only');
  const [reviewBy, setReviewBy] = useState(base?.review_by ?? '');
  const [triggers, setTriggers] = useState<string[]>(base?.triggers ?? ['new_system', 'law_amendment']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'record' | null>(null);
  const [declining, setDeclining] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [dropped, setDropped] = useState<string | null>(null);
  const gate = grid ? recordGate(grid) : { blocked: true, text: '' };
  const openCells = grid?.cells.filter((c) => c.open) ?? [];
  const blocked = openCells.length > 0;
  const choices = [...new Set([...JURISDICTION_CHOICES, ...jurisdictions])];
  const baseJur = sources.current?.legislation_on ? sources.current.jurisdictions : [];

  useEffect(() => { document.getElementById(`${uid}-h`)?.focus(); }, [uid]);

  const body = () => ({
    legislation_on: legislation, case_law_on: legislation && caseLaw, jurisdictions: legislation ? jurisdictions : [],
    accept_proposal_ids: accepted, rationale: rationale.trim() || null, rationale_visibility: visibility,
    review_by: reviewBy || null, triggers,
  });
  const save = async () => {
    if (busy) return;
    setBusy('save'); setError(null);
    try { await saveSourcesDraft(slug, body()); onDone(`v${v} draft saved`); }
    catch (e) { setError(errorText(e, 'The draft could not be saved.')); }
    finally { setBusy(null); }
  };
  // T4-H2: one gate message, checked before the rationale; a valid submit confirms the diff first.
  const record = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (blocked) {
      setErrors({});
      document.getElementById(OPEN_FIELDSET_ID)?.focus();
      return;
    }
    const errs: Record<string, string> = {};
    if (rationale.trim().length < 3) errs.rationale = 'Give a one-line rationale.';
    if (legislation && jurisdictions.length === 0) errs.jurisdictions = 'Choose at least one jurisdiction, or turn AI legislation off.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setConfirm(true);
  };
  const doRecord = async (): Promise<boolean> => {
    if (busy) return false;
    setBusy('record'); setError(null);
    try {
      await saveSourcesDraft(slug, body());
      await recordSourcesDecision(slug, { rationale: rationale.trim(), rationale_visibility: visibility, review_by: reviewBy || null, triggers });
      setConfirm(false);
      onDone(sources.current ? `Decision v${v} recorded · v${sources.current.version} kept in history` : `Decision v${v} recorded`, true);
      return true;
    } catch (err) {
      setConfirm(false);
      if (err instanceof ApiError && err.status === 409 && (err.body as { code?: string } | undefined)?.code === 'cells_open') {
        onDone('');
        setTimeout(() => document.getElementById(OPEN_FIELDSET_ID)?.focus(), 0);
      } else setError(errorText(err, 'The decision could not be recorded.'));
      return false;
    } finally { setBusy(null); }
  };
  const toggle = (list: string[], v2: string, on: boolean) => (on ? [...new Set([...list, v2])] : list.filter((x) => x !== v2));
  const changeOf = (p: DecisionView) => proposalChange(p, baseJur);
  // T4-H6: accepting a proposal applies its change; unticking one of its jurisdictions drops it (with a warning).
  const accept = (p: DecisionView, on: boolean) => {
    setAccepted((a) => toggle(a, p.decision_id, on));
    const adds = changeOf(p).adds;
    if (on && adds.length) { setLegislation(true); setJurisdictions((j) => [...new Set([...j, ...adds])]); }
    setDropped(null);
  };
  const setJur = (j: string, on: boolean) => {
    setJurisdictions((list) => toggle(list, j, on));
    setErrors((x) => ({ ...x, jurisdictions: '' }));
    if (on) return;
    const hit = sources.proposals.filter((p) => accepted.includes(p.decision_id) && changeOf(p).adds.includes(j));
    if (hit.length) {
      setAccepted((a) => a.filter((x) => !hit.some((p) => p.decision_id === x)));
      setDropped(`${jurisdictionName(j)} unticked · drops the accepted proposal “${hit[0]!.note ?? 'proposal'}”`);
    }
  };
  const diff = decisionDiff(sources.current, { legislation_on: legislation, case_law_on: legislation && caseLaw, jurisdictions });

  return (
    <section id="ai-editor" className="gp-ai-card" aria-labelledby={`${uid}-h`}>
      <form className="gp-form" onSubmit={record} noValidate>
        <div className="gp-ai-card-head">
          <h3 id={`${uid}-h`} className="gp-card-h" tabIndex={-1}>{`Decision v${v} · ${sources.draft ? 'draft' : 'new draft'}`}</h3>
          <span className="gp-ai-priv">Privileged &amp; Confidential</span>
        </div>
        {error ? <Alert variant="error">{error}</Alert> : null}

        <fieldset className="gp-ai-fieldset" id={OPEN_FIELDSET_ID} tabIndex={-1}>
          <legend>{`Open determinations (${openCells.length})`}</legend>
          {openCells.length ? (
            <ul className="gp-ai-open-list">
              {openCells.map((c, i) => {
                const next = openCells[i + 1] ?? openCells[i - 1] ?? null;
                return (
                  <li key={c.cell_id}>
                    <span>{grid ? cellTitle(grid, c) : c.cell_id}</span>
                    <Button compact id={decideButtonId(c.cell_id)} onClick={() => onOpenCell(c.cell_id, next ? decideButtonId(next.cell_id) : OPEN_FIELDSET_ID)}
                      aria-label={`Decide · ${grid ? cellTitle(grid, c) : c.cell_id}`}>Decide</Button>
                  </li>
                );
              })}
            </ul>
          ) : <p className="gp-dim">{grid?.decided_in_draft ? `All ${grid.decided_in_draft} in the v${v} draft` : 'None open'}</p>}
        </fieldset>

        <Checkbox id={`${uid}-first`} checked={legislation} disabled={locked}
          onChange={(e) => setLegislation(e.currentTarget.checked)} label={locked ? 'Track AI legislation · locked by org policy' : 'Track AI legislation'} />
        <Checkbox id={`${uid}-cl`} checked={legislation && caseLaw} disabled={locked || !legislation}
          onChange={(e) => setCaseLaw(e.currentTarget.checked)} label="Case-law signals" />
        {legislation ? (
          <fieldset className="gp-ai-fieldset" aria-describedby={errors.jurisdictions ? `${uid}-jur-err` : dropped ? `${uid}-drop` : undefined}>
            <legend>Jurisdictions</legend>
            <div className="gp-ai-inline">
              {choices.map((j) => (
                <Checkbox key={j} id={`${uid}-j-${j}`} checked={jurisdictions.includes(j)} label={jurisdictionName(j)} aria-invalid={!!errors.jurisdictions || undefined}
                  onChange={(e) => setJur(j, e.currentTarget.checked)} />
              ))}
            </div>
            {dropped ? <p className="gp-ai-warn" id={`${uid}-drop`} role="status">{dropped}</p> : null}
            {errors.jurisdictions ? <p className="gp-field-err" id={`${uid}-jur-err`} role="alert">{errors.jurisdictions}</p> : null}
          </fieldset>
        ) : null}

        {sources.proposals.length ? (
          <div>
            <h4 id={`${uid}-ph`} className="gp-ai-h4">Proposals</h4>
            <ul className="gp-ai-proposals" aria-labelledby={`${uid}-ph`}>
              {sources.proposals.map((p) => {
                const ch = changeOf(p);
                return (
                  <li key={p.decision_id}>
                    <Checkbox id={`${uid}-p-${p.decision_id}`} checked={accepted.includes(p.decision_id)}
                      label={ch.text ? `${p.note ?? 'Proposal'} · ${ch.text}` : `Acknowledge · ${p.note ?? 'proposal'}`}
                      onChange={(e) => accept(p, e.currentTarget.checked)} />
                    {ch.text ? null : <p className="gp-dim gp-ai-ack">Marks it reviewed · changes nothing</p>}
                    <div><Button compact onClick={() => setDeclining(p.decision_id)} aria-label={`Decline… · ${p.note ?? 'proposal'}`}>Decline…</Button></div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <Field id={`${uid}-rat`} label="Rationale · one line" required error={errors.rationale}>
          <Input value={rationale} maxLength={2000} onChange={(e) => { setRationale(e.currentTarget.value); setErrors((x) => ({ ...x, rationale: '' })); }} />
        </Field>
        <Field id={`${uid}-vis`} label="Rationale visibility">
          <Select value={visibility} onChange={(e) => setVisibility(e.currentTarget.value as typeof visibility)}>
            {(Object.keys(VISIBILITY_LABEL) as Array<keyof typeof VISIBILITY_LABEL>).map((x) => <option key={x} value={x}>{VISIBILITY_LABEL[x]}</option>)}
          </Select>
        </Field>
        <Field id={`${uid}-rev`} label="Re-review by">
          <Input type="date" value={reviewBy} onChange={(e) => setReviewBy(e.currentTarget.value)} />
        </Field>
        <fieldset className="gp-ai-fieldset">
          <legend>Also re-review on</legend>
          <div className="gp-ai-inline">
            {REREVIEW_TRIGGERS.filter((t) => t !== 'date').map((t) => (
              <Checkbox key={t} id={`${uid}-t-${t}`} checked={triggers.includes(t)} label={TRIGGER_LABEL[t] ?? t}
                onChange={(e) => setTriggers(toggle(triggers, t, e.currentTarget.checked))} />
            ))}
          </div>
        </fieldset>
        {blocked ? <p className="gp-ai-gate" id={`${uid}-gate`}>{gate.text}</p> : null}
        <div className="gp-actions gp-actions--end">
          <Button onClick={onClose}>Close draft</Button>
          <Button onClick={() => void save()} aria-disabled={!!busy || undefined}>{busy === 'save' ? 'Saving…' : 'Save draft'}</Button>
          <Button type="submit" variant="primary" aria-disabled={!!busy || blocked || undefined} aria-describedby={blocked ? `${uid}-gate` : undefined}>
            {busy === 'record' ? 'Recording…' : `Record decision v${v}`}
          </Button>
        </div>
      </form>
      <ActionDialog open={confirm} title={`Record decision v${v}?`} submitLabel={`Record v${v}`} onClose={() => setConfirm(false)} onSubmit={doRecord}
        busy={busy === 'record'} error={null}>
        <ul className="gp-ai-history" aria-label={`What v${v} changes`}>
          {(diff.length ? diff : [sources.current ? `Same sources as v${sources.current.version}` : 'The first recorded version']).map((x) => <li key={x}>{x}</li>)}
          {grid?.decided_in_draft ? <li>{`${grid.decided_in_draft} determination${grid.decided_in_draft === 1 ? '' : 's'} recorded`}</li> : null}
          {accepted.length ? <li>{`${accepted.length} proposal${accepted.length === 1 ? '' : 's'} accepted`}</li> : null}
        </ul>
      </ActionDialog>
      <DeclineDialog slug={slug} proposal={sources.proposals.find((p) => p.decision_id === declining) ?? null}
        onClose={() => setDeclining(null)} onDone={(m) => { setDeclining(null); setAccepted((a) => a.filter((x) => x !== declining)); onDone(m); }} />
    </section>
  );
}

function DeclineDialog({ slug, proposal, onClose, onDone }: { slug: string; proposal: DecisionView | null; onClose: () => void; onDone: (m: string) => void }) {
  const uid = useId();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); setErr(null); setError(null); }, [proposal?.decision_id]);
  return (
    <ActionDialog open={!!proposal} title={`Decline · ${proposal?.note ?? 'proposal'}`} submitLabel="Decline" danger onClose={onClose} busy={busy} error={error}
      onSubmit={async () => {
        if (reason.trim().length < 3) { setErr('Say why.'); return false; }
        setBusy(true);
        try { await declineSourcesProposal(slug, proposal!.decision_id, reason.trim()); onDone('Proposal declined'); return true; }
        catch (e) { setError(errorText(e, 'The proposal could not be declined.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id={`${uid}-r`} label="Reason" required error={err ?? undefined}>
        <Textarea rows={2} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setErr(null); }} />
      </Field>
    </ActionDialog>
  );
}

// ---- propose (owner / admin) ------------------------------------------------------------------

export function ProposeDialog({ slug, open, sources, onClose, onDone }: {
  slug: string; open: boolean; sources: ObligationSourcesView; onClose: () => void; onDone: (m: string) => void;
}) {
  const uid = useId();
  const [note, setNote] = useState('');
  const [add, setAdd] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNote(''); setAdd([]); setErr(null); setError(null); } }, [open]);
  // T4-H6: jurisdictions already tracked or already in an open proposal aren't offered again.
  const taken = new Set([...sources.effective.jurisdictions, ...sources.proposals.flatMap((p) => (p.legislation_on === false ? [] : p.jurisdictions))]);
  const offer = JURISDICTION_CHOICES.filter((j) => !taken.has(j));
  return (
    <ActionDialog open={open} title="Propose a change · to Legal" submitLabel="Send to Legal" onClose={onClose} busy={busy} error={error}
      onSubmit={async () => {
        if (note.trim().length < 3) { setErr('Say what should change and why.'); return false; }
        setBusy(true);
        try {
          await proposeSourcesChange(slug, add.length
            ? { legislation_on: true, jurisdictions: [...new Set([...sources.effective.jurisdictions, ...add])], note: note.trim() }
            : { note: note.trim() });
          onDone('Proposal sent · awaiting Legal'); return true;
        } catch (e) { setError(errorText(e, 'The proposal could not be sent.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id={`${uid}-n`} label="What should change, and the basis" required error={err ?? undefined}>
        <Textarea rows={3} value={note} onChange={(e) => { setNote(e.currentTarget.value); setErr(null); }} />
      </Field>
      {offer.length ? (
        <fieldset className="gp-ai-fieldset">
          <legend>Add jurisdictions (optional)</legend>
          <div className="gp-ai-inline">
            {offer.map((j) => (
              <Checkbox key={j} id={`${uid}-a-${j}`} checked={add.includes(j)} label={jurisdictionName(j)}
                onChange={(e) => setAdd(e.currentTarget.checked ? [...add, j] : add.filter((x) => x !== j))} />
            ))}
          </div>
        </fieldset>
      ) : null}
    </ActionDialog>
  );
}

// ---- asks: Legal (one law × system) and the org admin ------------------------------------------

export function AskLegalDialog({ slug, open, grid, legalName, onClose, onDone }: {
  slug: string; open: boolean; grid: AiGrid | null; legalName: string | null; onClose: () => void; onDone: (m: string) => void;
}) {
  const uid = useId();
  const cells = useMemo(() => (grid ? [...grid.cells].sort((a, b) => Number(b.open) - Number(a.open)) : []), [grid]);
  const [cell, setCell] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCell(cells[0]?.cell_id ?? ''); setMsg(''); setErr(null); setError(null); } }, [open, cells]);
  const who = legalName ?? 'Legal';
  return (
    <ActionDialog open={open} title={`Ask ${who}`} submitLabel={`Send to ${who}`} onClose={onClose} busy={busy} error={error}
      onSubmit={async () => {
        if (!cell) { setErr('Choose a law and system.'); return false; }
        setBusy(true);
        try {
          const r = await askLegalAboutCell(slug, cell, msg.trim() || undefined);
          onDone(r.reused ? `Already asked ${who} · under Waiting on others` : `Asked ${who} · under Waiting on others`); return true;
        } catch (e) { setError(errorText(e, 'The question could not be sent.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id={`${uid}-c`} label="About" required error={err ?? undefined}>
        <Select value={cell} onChange={(e) => { setCell(e.currentTarget.value); setErr(null); }}>
          {cells.map((c) => <option key={c.cell_id} value={c.cell_id}>{`${grid ? cellTitle(grid, c) : c.cell_id}${c.open ? ' · open' : ''}`}</option>)}
        </Select>
      </Field>
      <Field id={`${uid}-m`} label="Your question (optional)">
        <Textarea rows={3} value={msg} maxLength={2000} onChange={(e) => setMsg(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}

export function AskPolicyDialog({ slug, open, contact, onClose, onDone }: {
  slug: string; open: boolean; contact: string; onClose: () => void; onDone: (m: string) => void;
}) {
  const uid = useId();
  const [msg, setMsg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMsg(''); setError(null); } }, [open]);
  return (
    <ActionDialog open={open} title={`Ask ${contact} to allow AI legislation`} submitLabel={`Send to ${contact}`} onClose={onClose} busy={busy} error={error}
      onSubmit={async () => {
        setBusy(true);
        try {
          const r = await askOrgAdmin(slug, msg.trim() || undefined);
          onDone(r.reused ? `Already asked ${contact}` : `Asked ${contact} · under Waiting on others`); return true;
        } catch (e) { setError(errorText(e, 'The ask could not be sent.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id={`${uid}-m`} label="Why (optional)">
        <Textarea rows={3} value={msg} maxLength={2000} onChange={(e) => setMsg(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}

// ---- release rationale (Legal) ------------------------------------------------------------------

const RECIPIENTS: Array<[ReleaseRecipientKind, string]> = [
  ['auditor', 'Auditor'], ['trust_center', 'Trust center'], ['regulator', 'Regulator'], ['customer', 'Customer'], ['counsel', 'Outside counsel'], ['other', 'Other'],
];

export function ReleaseDialog({ slug, open, grid, preselect, onClose, onDone }: {
  slug: string; open: boolean; grid: AiGrid | null; preselect: string | null; onClose: () => void; onDone: (m: string) => void;
}) {
  const uid = useId();
  const recorded = (grid?.cells ?? []).filter((c) => c.determination?.state === 'recorded' && c.determination.outcome);
  const byLaw = (grid?.laws ?? []).map((l) => ({ law: l, cells: recorded.filter((c) => c.law_key === l.law_key) })).filter((g) => g.cells.length);
  const plus30 = () => new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
  const [kind, setKind] = useState<ReleaseRecipientKind>('auditor');
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [expires, setExpires] = useState(plus30());
  const [ids, setIds] = useState<string[]>([]);
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setKind('auditor'); setName(''); setPurpose(''); setExpires(plus30()); setIds(preselect ? [preselect] : []); setAck(false); setErrors({}); setError(null); }
  }, [open, preselect]);
  const toggle = (list: string[], add: string[], on: boolean) => (on ? [...new Set([...list, ...add])] : list.filter((x) => !add.includes(x)));
  const who = name.trim() || 'the recipient';
  const pinned = (
    <>
      <Checkbox id={`${uid}-ack`} checked={ack} aria-invalid={!!errors.ack || undefined} onChange={(e) => { setAck(e.currentTarget.checked); setErrors((x) => ({ ...x, ack: '' })); }}
        label={`I waive privilege for ${ids.length} determination${ids.length === 1 ? '' : 's'} to ${who} until ${expires ? fmtY(expires) : '…'}`}
        description={PRIVILEGE_WAIVER_TEXT} aria-describedby={errors.ack ? `${uid}-ack-err` : undefined} />
      {errors.ack ? <p className="gp-field-err" id={`${uid}-ack-err`} role="alert">{errors.ack}</p> : null}
    </>
  );
  return (
    <ActionDialog open={open} title="Release rationale" submitLabel="Waive privilege and release" danger onClose={onClose} busy={busy} error={error} pinned={pinned}
      onSubmit={async () => {
        const errs: Record<string, string> = {};
        if (name.trim().length < 2) errs.name = 'Name the recipient.';
        if (purpose.trim().length < 3) errs.purpose = 'Say what it is for.';
        if (!expires) errs.expires = 'Choose when the release ends.';
        if (!ids.length) errs.ids = 'Choose at least one determination.';
        if (!ack) errs.ack = 'Acknowledge the privilege waiver.';
        setErrors(errs);
        if (Object.keys(errs).length) return false;
        setBusy(true);
        try {
          await releaseRationale(slug, { recipient_kind: kind, recipient_name: name.trim(), purpose: purpose.trim(), determination_ids: ids,
            waiver_acknowledged: true, expires_at: new Date(`${expires}T23:59:59Z`).toISOString() });
          onDone(`Released ${ids.length} to ${name.trim()} until ${fmtY(expires)} · logged`); return true;
        } catch (e) { setError(errorText(e, 'The release could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id={`${uid}-k`} label="Recipient">
        <Select value={kind} onChange={(e) => setKind(e.currentTarget.value as ReleaseRecipientKind)}>
          {RECIPIENTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Select>
      </Field>
      <Field id={`${uid}-n`} label="Recipient name" required error={errors.name}>
        <Input value={name} onChange={(e) => { setName(e.currentTarget.value); setErrors((x) => ({ ...x, name: '' })); }} />
      </Field>
      <Field id={`${uid}-p`} label="Purpose" required error={errors.purpose}>
        <Input value={purpose} onChange={(e) => { setPurpose(e.currentTarget.value); setErrors((x) => ({ ...x, purpose: '' })); }} />
      </Field>
      <Field id={`${uid}-e`} label="Release ends" required error={errors.expires} hint="At most a year.">
        <Input type="date" value={expires} onChange={(e) => { setExpires(e.currentTarget.value); setErrors((x) => ({ ...x, expires: '' })); }} />
      </Field>
      {byLaw.map(({ law, cells }) => {
        const lawIds = cells.map((c) => c.determination!.determination_id);
        const all = lawIds.every((id) => ids.includes(id));
        return (
          <fieldset key={law.law_key} className="gp-ai-fieldset" aria-describedby={errors.ids ? `${uid}-ids-err` : undefined}>
            <legend>{law.label}</legend>
            <Checkbox id={`${uid}-all-${law.law_key}`} checked={all} label="Select all for this law"
              onChange={(e) => { setIds(toggle(ids, lawIds, e.currentTarget.checked)); setErrors((x) => ({ ...x, ids: '' })); }} />
            {cells.map((c) => {
              const id = c.determination!.determination_id;
              return (
                <Checkbox key={c.cell_id} id={`${uid}-d-${id}`} checked={ids.includes(id)}
                  label={`${grid!.systems.find((s) => s.system_id === c.system_id)?.name ?? c.system_id} · ${OUTCOME_LABEL[c.determination!.outcome!]}`}
                  onChange={(e) => { setIds(toggle(ids, [id], e.currentTarget.checked)); setErrors((x) => ({ ...x, ids: '' })); }} />
              );
            })}
          </fieldset>
        );
      })}
      {errors.ids ? <p className="gp-field-err" id={`${uid}-ids-err`} role="alert">{errors.ids}</p> : null}
    </ActionDialog>
  );
}

/** T4-H7: the releases list — each active one can be revoked (reason required). */
export function ReleasesDialog({ slug, open, releases, onClose, onDone, onNew }: {
  slug: string; open: boolean; releases: PrivilegeReleaseView[]; onClose: () => void; onDone: (m: string) => void; onNew: (() => void) | null;
}) {
  const uid = useId();
  const [revoking, setRevoking] = useState<PrivilegeReleaseView | null>(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setRevoking(null); setReason(''); setErr(null); setError(null); } }, [open]);
  if (revoking) {
    return (
      <ActionDialog open={open} title={`Revoke · ${revoking.recipient_name}`} submitLabel="Revoke release" danger onClose={onClose} busy={busy} error={error}
        headerBack={{ label: '← Back', onClick: () => { setRevoking(null); setErr(null); } }}
        onSubmit={async () => {
          if (reason.trim().length < 3) { setErr('Say why.'); return false; }
          setBusy(true);
          try { await revokeRelease(slug, revoking.release_id, reason.trim()); onDone(`Release to ${revoking.recipient_name} revoked · logged`); return true; }
          catch (e) { setError(errorText(e, 'The release could not be revoked.')); return false; }
          finally { setBusy(false); }
        }}>
        <Field id={`${uid}-r`} label="Reason" required error={err ?? undefined}>
          <Textarea rows={2} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setErr(null); }} />
        </Field>
      </ActionDialog>
    );
  }
  return (
    <ActionDialog open={open} title="Releases" submitLabel="" hideSubmit cancelLabel="Close" onClose={onClose} onSubmit={() => false}>
      {releases.length ? (
        <ul className="gp-ai-gaps" aria-label="Releases">
          {releases.map((r) => (
            <li key={r.release_id}>
              <span className="gp-ai-gap-t">{`${r.recipient_name} · ${r.determination_ids.length} determination${r.determination_ids.length === 1 ? '' : 's'}`}</span>
              <span className="gp-dim">{r.status === 'active' ? `Until ${fmtY(r.expires_at)}` : r.status === 'revoked' ? 'Revoked' : 'Expired'}</span>
              {r.status === 'active' ? <Button compact onClick={() => setRevoking(r)}>Revoke…</Button> : null}
            </li>
          ))}
        </ul>
      ) : <div className="gp-actions"><span className="gp-dim">No releases yet</span>{onNew ? <Button compact onClick={onNew}>Release rationale…</Button> : null}</div>}
    </ActionDialog>
  );
}
